import { fmtDate } from '@/lib/dates.js';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building2, Check, Edit2, Info, Loader2, Plus, Power, PowerOff, Trash2, X } from 'lucide-react';
import { adminSites, type AdminSiteRow } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { humanStatus } from '@/lib/humanise';
import { toast } from 'sonner';
import { ActionMenu, ActionItem } from '@/components/ui/ActionMenu';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * DREAM-8 — Sites administration.
 *
 * Lists every site on the platform (platform_admin / application_admin only).
 * Inline actions: rename, disable / enable, delete (with name confirmation).
 * "+ New Site" routes to the onboard form.
 */

export function SitesListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const [renaming, setRenaming] = useState<AdminSiteRow | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState<AdminSiteRow | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const list = useQuery({ queryKey: ['admin-sites'], queryFn: () => adminSites.list() });

  const onError = (err: unknown) =>
    toast.error(errorText(err, 'Action failed'));

  const renameMut = useMutation({
    mutationFn: () => adminSites.rename(renaming!.site_id, renameValue.trim()),
    onSuccess: () => {
      toast.success(`Site renamed to "${renameValue.trim()}"`);
      setRenaming(null); setRenameValue('');
      qc.invalidateQueries({ queryKey: ['admin-sites'] });
    },
    onError,
  });

  const statusMut = useMutation({
    mutationFn: ({ siteId, action }: { siteId: string; action: 'enable' | 'disable' }) =>
      adminSites.setStatus(siteId, action),
    onSuccess: (_data, vars) => {
      toast.success(vars.action === 'enable' ? 'Site enabled' : 'Site disabled');
      qc.invalidateQueries({ queryKey: ['admin-sites'] });
      // Re-sync the caller's own session immediately — otherwise the
      // SiteSwitcher dropdown only picks up a disabled/re-enabled site on
      // the next ~13-minute background token refresh.
      void refreshSession();
    },
    onError,
  });

  const deleteMut = useMutation({
    mutationFn: () => adminSites.remove(deleting!.site_id, deleteConfirm),
    onSuccess: () => {
      toast.success(`Site "${deleting?.name}" deleted`);
      setDeleting(null); setDeleteConfirm('');
      qc.invalidateQueries({ queryKey: ['admin-sites'] });
    },
    onError,
  });

  function handleRenameSubmit(e: FormEvent) {
    e.preventDefault();
    if (!renaming || renameValue.trim().length < 2) return;
    renameMut.mutate();
  }

  function handleDeleteSubmit(e: FormEvent) {
    e.preventDefault();
    if (!deleting || deleteConfirm !== deleting.name) return;
    deleteMut.mutate();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="Sites"
        subtitle="Every site on the platform. Platform admins can rename, disable, or delete sites."
        actions={
          <button type="button" onClick={() => navigate('/admin/sites/new')} className="btn-primary">
            <Plus className="h-4 w-4" /> New site
          </button>
        }
      />

      <div className="card overflow-hidden">
        {list.isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sites…
          </div>
        )}
        {list.isError && (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(list.error)}`}>
            {isPermissionError(list.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{errorText(list.error, 'Failed to load sites')}</span>
          </div>
        )}
        {list.data && list.data.sites.length === 0 && (
          <div className="p-8 text-center text-sm text-ink-muted">
            <Building2 className="mx-auto mb-2 h-10 w-10 opacity-50" />
            No sites yet. <Link to="/admin/sites/new" className="text-blue underline">Create the first site</Link>.
          </div>
        )}
        {list.data && list.data.sites.length > 0 && (
          <table className="min-w-full text-sm">
            <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Bucket</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.sites.map((s) => (
                <tr key={s.site_id} className="border-b border-surface-border last:border-0">
                  <td className="px-3 py-2 font-medium">
                    <Link to={`/admin/sites/${s.site_id}`} className="text-blue hover:text-navy hover:underline">
                      {s.name}
                    </Link>
                    {s.reference_number && (
                      <div className="font-mono text-xs font-normal text-ink-muted">{s.reference_number}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-muted truncate max-w-xs" title={s.s3_bucket_name}>
                    {s.s3_bucket_name}
                  </td>
                  <td className="px-3 py-2">
                    <span className={s.status === 'active' ? 'badge badge-success' : 'badge badge-warn'}>
                      {humanStatus(s.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-muted">
                    {fmtDate(s.created_at)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <ActionMenu label={`Actions for ${s.name}`}>
                      <ActionItem
                        icon={Edit2}
                        onClick={() => { setRenaming(s); setRenameValue(s.name); }}
                      >
                        Rename
                      </ActionItem>
                      {s.status === 'active' ? (
                        <ActionItem
                          icon={PowerOff}
                          onClick={() => statusMut.mutate({ siteId: s.site_id, action: 'disable' })}
                        >
                          Disable
                        </ActionItem>
                      ) : (
                        <ActionItem
                          icon={Power}
                          onClick={() => statusMut.mutate({ siteId: s.site_id, action: 'enable' })}
                        >
                          Enable
                        </ActionItem>
                      )}
                      <ActionItem
                        icon={Trash2}
                        danger
                        onClick={() => { setDeleting(s); setDeleteConfirm(''); }}
                      >
                        Delete…
                      </ActionItem>
                    </ActionMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Rename modal */}
      {renaming && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={handleRenameSubmit} className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">Rename site</h2>
              <button type="button" onClick={() => setRenaming(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div>
              <label className="label text-xs" htmlFor="rename-name">New name</label>
              <input
                id="rename-name" type="text" required minLength={2} maxLength={120}
                className="input" value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setRenaming(null)}>Cancel</button>
              <button type="submit" className="btn-primary"
                disabled={renameMut.isPending || renameValue.trim() === renaming.name}>
                {renameMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete modal */}
      {deleting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={handleDeleteSubmit} className="card w-full max-w-md overflow-hidden p-0">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-surface-border px-6 py-4">
              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-danger-600" />
                <h2 className="text-base font-semibold text-danger-700">Delete site</h2>
              </div>
              <button
                type="button"
                onClick={() => { setDeleting(null); setDeleteConfirm(''); }}
                className="rounded p-1 text-ink-muted hover:bg-surface-muted hover:text-ink-primary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4 px-6 py-5">
              {deleting.site_id === user?.activeSiteId ? (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Cannot delete the active site</p>
                    <p className="mt-1 text-amber-700">
                      Switch to a different site using the site selector at the top, then come back to delete <strong>{deleting.name}</strong>.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-ink-muted">
                    This permanently deletes{' '}
                    <strong className="text-ink-primary">{deleting.name}</strong>{' '}
                    and tries to remove its S3 bucket. Refuses if the site has users, studies, or subjects.
                  </p>
                  <div>
                    <label className="label text-xs mb-1 block">Type the site name to confirm</label>
                    <input
                      type="text"
                      required
                      className="input"
                      value={deleteConfirm}
                      placeholder={deleting.name}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      autoFocus
                    />
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 border-t border-surface-border px-6 py-4">
              <button type="button" className="btn-ghost" onClick={() => { setDeleting(null); setDeleteConfirm(''); }}>
                Cancel
              </button>
              {deleting.site_id !== user?.activeSiteId && (
                <button
                  type="submit"
                  className="btn-danger"
                  disabled={deleteMut.isPending || deleteConfirm !== deleting.name}
                >
                  {deleteMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
